import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { EntryPoint, EntryPointService, JobFilter } from '@mirage-projects/cw-commons';
import { Job, PcvizUploadConfig, Pipeline, ResultPage } from '@mirage-projects/cw-models';

const CHECK_PROCESS_INTERVAL = 30000 as const;

@Injectable({
    providedIn: 'root',
})
export class AppJobService {
    constructor(
        private http: HttpClient,
        private entryPointService: EntryPointService
    ) {}

    /**
     * Get the job by id
     * @param id Job id
     */
    get(id: string): Observable<Job> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS, id);
        return this.http.get<Job>(`${entryPoint}`, {}).pipe(catchError((err) => throwError(err)));
    }

    /**
     * Filter the jobs
     * @param jobFilter Filter to apply
     */
    filter(jobFilter: JobFilter): Observable<ResultPage<Job>> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS);
        const queryParams = jobFilter?.getQueryParams();
        return this.http.get<ResultPage<Job>>(`${entryPoint}`, { params: queryParams });
    }

    /**
     * Create and start a job
     * @param pipeline Pipeline to create the job
     */
    createAndStart(pipeline: Pipeline): Observable<Job> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS);
        return this.http.post<Job>(`${entryPoint}?action=create_and_start`, pipeline).pipe(catchError((err) => throwError(err)));
    }

    /**
     * Start a job
     * @param id Job id
     */
    start(id: string): Observable<Job> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS, id);
        return this.http.post<Job>(`${entryPoint}start/`, {}).pipe(catchError((err) => throwError(err)));
    }

    /**
     * Cancel a job
     * @param id Job id
     */
    cancel(id: string): Observable<Job> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS, id);
        return this.http.post<Job>(`${entryPoint}cancel/`, {}).pipe(catchError((err) => throwError(err)));
    }

    /**
     * Estimate a job
     * @param id Job id
     */
    estimate(id: string): Observable<Job> {
        const entryPoint = this.entryPointService.getResource(EntryPoint.JOBS, id);
        return this.http.post<Job>(`${entryPoint}estimate/`, {}).pipe(catchError((err) => throwError(err)));
    }

    /**
     * Starts process status checking
     * @param process  Job to check
     * @param config Upload configuration
     */
    startCheckProcessStatus(process: Job, config: PcvizUploadConfig): number {
        const interval = window.setInterval(() => {
            this.get(process.id?.toString()).subscribe((job) => {
                if (config.upload) {
                    config.upload.status = job.status;
                }
                if (job.status !== 'STARTED' && job.status !== 'PENDING') {
                    this.stopCheckProcessStatus(interval);
                }
            });
        }, CHECK_PROCESS_INTERVAL);

        return interval;
    }

    /**
     * Stop process status checking
     * @param interval Interval id to stop
     */
    stopCheckProcessStatus(interval: number): void {
        clearInterval(interval);
    }
}
